/** @jsxImportSource @emotion/react */
import { RED_CREEK, WHITE } from "../../../../styles/colors";
import { Entitlement, EntitlementShare } from "../../models";
import { checkEntitlementStatus, REDEEMABLE_OPTIONS } from "../module";
import { UNSELECTABLE_TEXT } from "../../../../styles/styles";
import React from "react";
import { ROW_SPACING_NARROW } from "../../../../forms/styles";
import expand from '../../../../assets/icons/circlePlus.svg';
import collapse from '../../../../assets/icons/circleMinus.svg';
import { getResource } from "../../../../language/resources/getResource";
import { MediumNeutral } from "../../../../forms/elements/buttons";
import { FnFRedemptionSummaryTable } from "./FnFRedemptionSummary";
import { GuestDetails } from "../../../guestDetails";
import { FNF_PAGES } from "./module";
import { css } from "@emotion/react";

const styles = {
  actionButtonSpacing: css`
    display: inline-flex; 
    flex-direction: row; 
    ${ROW_SPACING_NARROW}; 
    justify-content: center;
    min-width: 225px;
    width: 100%;
  `,
  text: css`
    text-align: center;  
  `,
  noEntitlements: css`
    width: 100%; 
    display: flex; 
    justify-content: center
  `,
  entitlementsOverview: css`
    display: flex; 
    flex-direction: row; 
    align-items: center;
  `,
  entitlementInfoExpandButton: css`
    opacity: 75%; 
    border: none;
    background-color: transparent;
    cursor: pointer; 
    &:hover {
      opacity: 100%;
    }
  `,
  entitlementInfoContainer: css`
    width: 100%; 
    background-color: transparent; 
    padding: 5px; 
    display: flex; 
    flex-wrap: wrap;
  `,
  expandButton: css`
    margin-left: -5px;
    margin-right: 5px;
    width: 17px;
    height: 17px;
    ${UNSELECTABLE_TEXT};
  `,
}

const blackedOut = css`
    td {
      text-align: center;  
    }
    background-color: ${RED_CREEK} !important;
    color: ${WHITE};
    text-align: center;
`;

type Props = {
  guest: GuestDetails;
  entitlement: Entitlement;
  allowCorrectiveRedemption: boolean;
  entitlementShares: EntitlementShare[];
  isLoadingEntitlementShares: boolean;
  entitlementSharesError: boolean;
  setCurrentPage: (page: FNF_PAGES) => void;
  setSelectedEntitlement: (entitlement: Entitlement) => void;
  resources: Record<string, any>;
};


/**
 *  Renders row for Friends and Family entitlements
  *  @param props
  *  @constructor
  *  @param guest - Guest details
  *  @param entitlement - Entitlement details
  *  @param allowCorrectiveRedemption - Flag to allow corrective redemption
  *  @param entitlementShares - Entitlement shares
  *  @param isLoadingEntitlementShares - Flag to indicate if entitlement shares are loading
  *  @param entitlementSharesError - Flag to indicate if there was an error loading entitlement shares
  *  @param setCurrentPage - Function to set the current page
  *  @param setSelectedEntitlement - Function to set the selected entitlement
  *  @param resources - Language resources
 */
export const FnFEntitlementsRow = (props: Props) => {

  const [isOpenRedemptionHistory, setIsOpenRedemptionHistory] = React.useState(false);

  const selectReturnEntitlementDays = (entitlement: Entitlement) => {
    props.setSelectedEntitlement(entitlement);
    props.setCurrentPage(FNF_PAGES.RETURN_DAYS);
  }

  const selectRedeemEntitlementDays = (entitlement: Entitlement) => {
    props.setSelectedEntitlement(entitlement);
    props.setCurrentPage(FNF_PAGES.REDEEM_DAYS);
  }
    
  const isRedeemable = props.entitlement.available > 0;
  const isReturnable = props.allowCorrectiveRedemption && props.entitlement.used > 0;
  const isBlackedOut = checkEntitlementStatus(props.entitlement, props.allowCorrectiveRedemption) === REDEEMABLE_OPTIONS.BLACKED_OUT;
    
  return (
    <React.Fragment key={props.entitlement.entitlementId}>
      <tr key={props.entitlement.entitlementId}>
        <td>
          {
            <div css={styles.entitlementsOverview}>
              <button css={styles.entitlementInfoExpandButton} 
                      onClick={() => setIsOpenRedemptionHistory(!isOpenRedemptionHistory)}>
                <img src={isOpenRedemptionHistory ? collapse : expand}
                    alt="Open"
                    title={getResource(props.resources, isOpenRedemptionHistory ? 'FNF_TABLE_COLLAPSE_REDEMPTION_HISTORY' : 'FNF_TABLE_EXPAND_REDEMPTION_HISTORY')}
                    css={styles.expandButton}/>
              </button>
              <span>{props.entitlement.entitlementDescription}</span>
            </div>
          }
        </td>
        <td css={isBlackedOut ? blackedOut : styles.text} title={isBlackedOut ? getResource(props.resources, 'FNF_TABLE_BLACKED_OUT') : ''}>
          {props.entitlement.maxAllowed - props.entitlement.used}
        </td>
        <td css={css`text-align: center;`}>{props.entitlement.used}</td>
        {
            <td css={css`padding: 10px 0px !important;`}>
              <div css={styles.actionButtonSpacing}>
                <MediumNeutral 
                  onClick={()=>selectRedeemEntitlementDays(props.entitlement)} 
                  disabled={!isRedeemable}
                  title={!isRedeemable ? getResource(props.resources, 'FNF_TABLE_ALL_USED') : ''}>
                  {getResource(props.resources, 'FNF_TABLE_REDEEM')}
                </MediumNeutral>
                {
                  props.allowCorrectiveRedemption &&
                  <MediumNeutral 
                    onClick={()=>selectReturnEntitlementDays(props.entitlement)}
                    disabled={!isReturnable}
                    title={!isReturnable ? getResource(props.resources, 'FNF_TABLE_NONE_USED') : ''}>
                    {getResource(props.resources, 'FNF_TABLE_RETURN')}
                  </MediumNeutral>
                }
              </div>
            </td>
        }
      </tr>
      {
        isOpenRedemptionHistory && 
        <tr css={css`background-color: transparent !important;`}>
          <td colSpan={4}>
            <FnFRedemptionSummaryTable 
              guest={props.guest} 
              entitlementShares={props.entitlementShares} 
              isLoadingEntitlementShares={props.isLoadingEntitlementShares} 
              entitlementSharesError={props.entitlementSharesError} 
              redemptionHistory={props.entitlement.redemptionHistory}/>
          </td>
        </tr>
      }
    </React.Fragment>
  );
};
